import React from 'react';
import { MessageCircle, Calendar, CheckCircle, Shield, Clock, Tag, Briefcase, Building2, Archive } from 'lucide-react';
import ServicePageLayout from '../components/ServicePageLayout';

const OFFICE_BEFORE = 'https://images.unsplash.com/photo-1627290218710-ff6006222f75?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const OFFICE_AFTER = 'https://images.unsplash.com/photo-1542081403278-ba5973c25c7a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';

const data = {
  seo: {
    title: 'Büroräumung Wien » Fixpreis ✓ Kurzfristig – Büroauflösung Wien',
    description: 'Professionelle Büroräumung Wien ✓ Büromöbel & Akten entsorgen ✓ Besenreine Übergabe ✓ Verbindlicher Fixpreis. Gewerberäumung & Betriebsauflösung – Termin noch diese Woche.',
    keywords: 'Büroräumung Wien, Büroauflösung Wien, Gewerberäumung Wien, Büromöbel entsorgen Wien, Betriebsauflösung Wien, Büro räumen Wien Kosten',
    canonical: '/bueroraeumung-wien',
    h1short: 'Büroräumung Wien',
  },
  schemas: [{
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Büroräumung Wien',
    'provider': { '@type': 'LocalBusiness', 'name': 'Wohnungsräumung Wien' },
    'areaServed': 'Wien',
    'description': 'Professionelle Büroräumung und Büroauflösung in Wien. Büromöbel, Akten und IT-Equipment fachgerecht entsorgen – mit Fixpreisgarantie und besenreiner Übergabe.',
    'offers': { '@type': 'Offer', 'price': '490', 'priceCurrency': 'EUR' },
  }],
  hero: {
    badge: 'Termine kurzfristig möglich',
    headline: (
      <>
        <span>Büroräumung<br />Wien</span>
        <br />
        <span className="text-[#E8722A]">zum Fixpreis.</span>
      </>
    ),
    subline: 'Mietvertrag läuft. Wir räumen. Sie sparen.',
    bullets: [
      'Büromöbel, Regale, Akten, IT – alles weg. Einmal anfragen.',
      'Fixpreis nach Besichtigung – kein Tagessatz, keine Überraschungen',
      'Kurzfristige Termine – auch bei Betriebsauflösung oder Insolvenz',
      'Datenschutzkonforme Aktenentsorgung auf Wunsch',
      'Besenreine Übergabe an den Vermieter. Immer inklusive.',
    ],
    imageSrc: OFFICE_BEFORE,
    imageAlt: 'Büroräumung Wien – Zu räumendes Büro mit Möbeln',
  },
  formTitle: 'Büroräumung anfragen –',
  serviceName: 'Büroräumung',
  serviceCard: {
    heading: 'Büroräumung Wien – effizient, diskret, besenrein',
    paragraphs: [
      'Mietvertrag kündigt. Betrieb wird aufgelöst. Oder der Umzug ins Homeoffice steht an. Was auch immer der Grund ist – ein altes Büro kostet jeden Tag extra Miete, solange es nicht geräumt ist.',
      'Wir räumen Büros jeder Größe in Wien und Umgebung. Vom Einzelbüro bis zur ganzen Etage. Schreibtische, Aktenschränke, Regale, Server, Drucker – alles wird fachgerecht abtransportiert und entsorgt. Sie bekommen den Schlüssel zurück, bevor die Rechnung drückt.',
    ],
    listItems: [
      'Büromöbel, Schreibtische, Stühle, Regale & Schränke vollständig entfernt',
      'IT-Equipment, Drucker und Elektroschrott fachgerecht entsorgt',
      'Akten und Unterlagen datenschutzkonform vernichtet (auf Wunsch)',
      'Wertanrechnung für gut erhaltene Büromöbel – spart bares Geld',
      'Besenreine Übergabe an Vermieter oder Hausverwaltung',
      'Kurzfristige Termine – auch bei Insolvenz oder Betriebsaufgabe',
    ],
    imageSrc: OFFICE_AFTER,
    imageAlt: 'Geräumtes Büro Wien – besenrein übergeben',
  },
  benefits: [
    { icon: <Shield size={22} />, title: 'Fixpreisgarantie', desc: 'Kein Tagessatz, kein Stundenlohn. Verbindlicher Festpreis nach Besichtigung – egal wie lange es dauert.' },
    { icon: <Clock size={22} />, title: 'Schnelle Termine', desc: 'Jeden Tag Büroräume offen = Miete. Wir kommen oft noch diese Woche und räumen in einem Zug durch.' },
    { icon: <Tag size={22} />, title: 'Wertanrechnung', desc: 'Gut erhaltene Büromöbel werden bewertet und direkt vom Preis abgezogen. Weniger zahlen für dasselbe Ergebnis.' },
    { icon: <CheckCircle size={22} />, title: 'Besenreine Übergabe', desc: 'Vermieter und Hausverwaltung bekommen den Raum besenrein zurück. Standard – ohne Aufpreis.' },
  ],
  process: [
    { icon: <MessageCircle size={24} />, title: 'Anfrage senden', desc: 'Formular ausfüllen oder kurz anrufen – 2 Minuten. Keine Verpflichtung, kein Risiko.' },
    { icon: <Calendar size={24} />, title: 'Besichtigung & Fixpreis', desc: 'Wir kommen ins Büro, schauen alles an und geben Ihnen noch heute einen verbindlichen Festpreis.' },
    { icon: <CheckCircle size={24} />, title: 'Räumung & Übergabe', desc: 'Unser Team räumt das Büro komplett – inklusive Entsorgung und besenreiner Übergabe.' },
  ],
  pricing: [
    { label: 'Kleines Büro (1–3 Räume)', price: 'ab €490', icon: <Briefcase size={20} />, highlight: false },
    { label: 'Mittleres Büro (4–8 Räume)', price: 'ab €890', icon: <Building2 size={20} />, highlight: true },
    { label: 'Große Etage / Gewerbe', price: 'auf Anfrage', icon: <Archive size={20} />, highlight: false },
  ],
  faq: [
    { q: 'Was kostet eine Büroräumung in Wien?', a: 'Kleine Büros (1–3 Räume) ab €490, mittlere Büros ab €890. Bei größeren Etagen oder ganzen Gewerberäumungen erstellen wir nach der Besichtigung ein individuelles Angebot. Immer als verbindlicher Fixpreis – kein Tagessatz, keine Stunden.' },
    { q: 'Wie schnell können Sie ein Büro räumen?', a: 'Sehr schnell. In den meisten Fällen sind wir noch diese Woche verfügbar. Gerade bei laufenden Mietverträgen ist Tempo entscheidend – das wissen wir. Einfach anrufen, wir finden einen Termin.' },
    { q: 'Kümmern Sie sich auch um Akten und IT-Equipment?', a: 'Ja. Dokumente und Unterlagen können auf Wunsch datenschutzkonform vernichtet werden. IT-Equipment wie Computer, Server und Drucker wird fachgerecht und umweltgerecht entsorgt.' },
    { q: 'Was passiert mit gut erhaltenen Büromöbeln?', a: 'Wir bewerten brauchbare Möbel vor Ort und ziehen den Wert direkt vom Gesamtpreis ab. Das nennt sich Wertanrechnung – und es spart Ihnen bares Geld beim Fixpreis.' },
    { q: 'Ist die Übergabe wirklich besenrein?', a: 'Ja. Immer. Ohne Aufpreis. Nach jeder Büroräumung übergeben wir den Raum sauber an den Vermieter oder die Hausverwaltung – das ist unser Standard.' },
    { q: 'Funktioniert das auch bei Insolvenz oder Betriebsaufgabe?', a: 'Ja. Wir kennen diese Situationen und arbeiten schnell, diskret und ohne großes Aufhebens. Einfach anfragen – wir finden gemeinsam einen Weg.' },
  ],
  relatedServices: [
    { title: 'Wohnungsräumung', slug: '/wohnungsraeumung-wien', icon: <Archive size={18} /> },
    { title: 'Haushaltsauflösung', slug: '/haushaltsaufloesung-wien', icon: <CheckCircle size={18} /> },
    { title: 'Kellerräumung', slug: '/kellerraeumung-wien', icon: <Building2 size={18} /> },
    { title: 'Nachlassräumung', slug: '/nachlassraeumung-wien', icon: <Briefcase size={18} /> },
  ],
};

export default function BueroRaeumungPage() {
  return <ServicePageLayout data={data} />;
}
